import React, { type ReactNode, Suspense, useCallback, useMemo } from 'react';

import { graphql, useLazyLoadQuery } from 'react-relay';

import { fg } from '@atlaskit/platform-feature-flags';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import UFOSegment from '@atlaskit/react-ufo/segment';
import { getTrialDaysLeft } from '@atlassian/upgrades-and-purchases/get-trial-days-left';

import type { DialogId } from '../analytics/types';
import { bytesToGigabytes } from '../services/bytes-to-gigabytes';
import {
	EditionAwarenessContext,
	type EditionAwarenessContextType,
} from '../services/edition-awareness-context';
import { ExperimentContext, ExperimentContextClass } from '../services/experiment-context';
import { getAutomationFreeLimitStatus } from '../services/get-automation-free-limit-status';
import { getProductCollection } from '../services/get-product-collection';
import { getQueryConfig } from '../services/get-query-config';
import { getSiteTraitValues } from '../services/get-trait-values';
import { isEnrolledInConfluenceUpgradeFreePoolTargetExperiment } from '../services/is-enrolled-in-confluence-upgrade-free-pool-target-experiment';
import { isProductOneOf } from '../services/is-product-one-of';
import { dialogIdToKey } from '../services/use-mark-dialog-as-seen';
import { useOnAttemptToUpgrade } from '../services/use-on-attempt-to-upgrade';
import type {
	Action,
	ActionId,
	Edition,
	Environment,
	Flag,
	LastKnownTrial,
	PremiumWrapperProps,
	Product,
	Trait,
	TraitsForContext,
	UserRole,
} from '../types';

import type { editionAwarenessQuery } from './__generated__/editionAwarenessQuery.graphql';
import { AudienceAndTiming } from './audience-and-timing';
import { ErrorBoundary } from './error-boundary';
import { Spotlights } from './spotlights';

function getBillingSourceSystem(typename?: string | null) {
	switch (typename) {
		case 'CcpEntitlement':
			return 'CCP';
		case 'HamsEntitlement':
			return 'HAMS';
	}
}

export type EditionAwarenessProps = {
	accountId?: string | null;
	/**
	 * This is used to inform the component that it can render, dependencies that need to be fetched
	 * outside of product should use this for the loading state.
	 *
	 * The reason for passing through `canRender` and not just returning `null` when `canRender` is false
	 * is to ensure that the edition awareness query can run as early as possible. This means that if the parameters being
	 * passed through are used in the query we can not use `canRender` for those as the query will run before we have
	 * the values.
	 */
	canRender: boolean;
	cloudId: string;
	edition: Edition;
	environment?: Environment;
	/**
	 * Get the in product action associated with the action id if available
	 */
	getAction: (id: ActionId) => Promise<Action<ActionId> | null>;
	/**
	 * Dismisses a flag from in-product
	 */
	hideFlag?: (id: string) => void;
	/** @deprecated fetching reverse trial state should be fetched within the edition awareness component */
	isInReverseTrial?: boolean;
	/** @deprecated avoid auto opening the dialogs */
	onAutoOpen?: () => void;
	/** @deprecated actions from within product will move to a more generic approach */
	onOpenOnboardingMenu?: () => void;
	/** @deprecated likely to be removed in the future */
	premiumFeatureHighlightToggle?: ReactNode;
	/**
	 * Confluence in premium has a lot of specific packages that are wired up with the button,
	 * we're therefore passing it through to edition awareness to prevent fragmenting the implementation
	 * of those features (e.g. needing to replicate/shift the logic for button border nudges into platform)
	 *
	 * @deprecated this will be removed, anything new should be added directly inside of the package and not in the product
	 */
	PremiumWrapper?: (props: PremiumWrapperProps) => ReactNode;
	product: Product;
	/** @deprecated avoid auto opening the dialogs */
	shouldAutoOpen?: boolean;
	/**
	 * Calls the in-product flag provider to show the flag to the customer
	 */
	showFlag?: (flag: Flag) => void;
	siteTraits?: Trait[];
	userRoles?: UserRole[];
};

/**
 * Edition awareness is used to communicate to customers about the current edition they have and if they should
 * - Add payment details
 * - Upgrade their edition
 *
 * This component deals with creating the context which is then used throughout the package to:
 * - Track analytics
 * - Know which product/edition/cloud id is being used
 * - Know when to show/hide the button
 */
function EditionAwarenessImpl(props: EditionAwarenessProps) {
	const {
		cloudId,
		product,
		edition,
		accountId,
		userRoles: userRolesFromProduct,
		environment,
		siteTraits = [],
		canRender,
		isInReverseTrial: isInReverseTrialFromProduct,
		onOpenOnboardingMenu,
		premiumFeatureHighlightToggle,
		shouldAutoOpen,
		onAutoOpen,
		PremiumWrapper,
		getAction,
		showFlag,
		hideFlag,
	} = props;

	const queryConfig = getQueryConfig(product, edition, cloudId, accountId);

	const data = useLazyLoadQuery<editionAwarenessQuery>(
		graphql`
			query editionAwarenessQuery(
				$cloudId: ID!
				$productKey: String!
				$shouldCheckForDirectBuyChangeOffering: Boolean!
				$shouldCheckForTrial: Boolean!
				$shouldSkip: Boolean!
				$shouldCheckForChargeQuantities: Boolean!
				$entitlementFilter: CommerceEntitlementFilter
				$chargeElement: String!
				$shouldCheckBillingAdmin: Boolean!
				$shouldCheckForStorageUsageJira: Boolean!
				$applicationKey: JiraApplicationKey!
				$shouldCheckForStorageUsageConfluence: Boolean!
				$accountId: ID!
				$shouldCheckForUserEmail: Boolean!
				$shouldCheckForStorageLimit: Boolean!
				$shouldCheckForPaymentDetails: Boolean!
				$shouldIncludeProfile: Boolean!
				$shouldCheckUserPreferences: Boolean!
				$onboardingStateKeys: [String!]!
			) {
				jira @include(if: $shouldCheckForStorageUsageJira) {
					attachmentStorageAllowed(cloudId: $cloudId, applicationKey: $applicationKey)
						@include(if: $shouldCheckForStorageLimit)
					attachmentStorageIsUnlimited(cloudId: $cloudId, applicationKey: $applicationKey)
						@include(if: $shouldCheckForStorageLimit)
					attachmentStorageUsed(cloudId: $cloudId, applicationKey: $applicationKey)
				}

				confluence_storage(cloudId: $cloudId) @include(if: $shouldCheckForStorageUsageConfluence) {
					bytesUsed
				}

				tenantContexts(cloudIds: [$cloudId]) @skip(if: $shouldSkip) {
					entitlementInfo(hamsProductKey: $productKey) {
						entitlementId
						entitlement(where: $entitlementFilter) {
							id
							__typename
							experienceCapabilities {
								changeOfferingV2(offeringName: "Standard") {
									isAvailableToUser
									experienceUrl
								}

								... on CcpEntitlementExperienceCapabilities
									@include(if: $shouldCheckForDirectBuyChangeOffering) {
									changeOfferingV2WithSkipTrial: changeOfferingV2(
										offeringName: "Standard"
										skipTrial: true
									) {
										isAvailableToUser
										experienceUrl
									}
								}
							}

							latestUsageForChargeElement(chargeElement: $chargeElement)
								@include(if: $shouldCheckForChargeQuantities)

							preDunning @include(if: $shouldCheckForTrial) {
								status
							}

							subscription @include(if: $shouldCheckForTrial) {
								trial {
									timeLeft
									startTimestamp
									endTimestamp
								}
								accountDetails {
									invoiceGroup {
										invoiceable
										experienceCapabilities {
											configurePaymentV2 {
												experienceUrl
												isAvailableToUser
											}
										}
									}
								}
							}

							offering @include(if: $shouldCheckForTrial) {
								trial {
									lengthDays
								}
								... on CcpOffering {
									key
								}
							}

							transactionAccount @include(if: $shouldCheckBillingAdmin) {
								key
								isCurrentUserBillingAdmin
							}

							transactionAccount @include(if: $shouldCheckForPaymentDetails) {
								isBillToPresent
							}

							relatesFromEntitlements {
								relationshipType
								entitlementId
							}

							entitlementProfile @include(if: $shouldIncludeProfile) {
								lastKnownTrial {
									trialType
									trialTrigger
									trialEndTimeStamp
									createdAt
									offeringId
								}
							}
						}
					}
				}

				user(accountId: $accountId) @include(if: $shouldCheckForUserEmail) {
					... on AtlassianAccountUser {
						email
					}
				}

				userPreferences @include(if: $shouldCheckUserPreferences) {
					onboardingState(key: $onboardingStateKeys) {
						key
						value
					}
				}
			}
		`,
		queryConfig,
	);

	const entitlementInfo = data?.tenantContexts?.[0]?.entitlementInfo;
	const entitlement = entitlementInfo?.entitlement;
	const entitlementId = entitlementInfo?.entitlementId;

	const invoiceableStatus =
		typeof entitlement?.subscription?.accountDetails?.invoiceGroup?.invoiceable === 'boolean'
			? entitlement.subscription.accountDetails.invoiceGroup.invoiceable
				? 'INVOICEABLE'
				: 'NOT_INVOICEABLE'
			: 'UNKNOWN';
	const isConfigurePaymentAvailable =
		entitlement?.subscription?.accountDetails?.invoiceGroup?.experienceCapabilities
			?.configurePaymentV2?.isAvailableToUser === true;
	const addPaymentDetailsExperienceUrl =
		entitlement?.subscription?.accountDetails?.invoiceGroup?.experienceCapabilities
			?.configurePaymentV2?.experienceUrl;

	const canAddPaymentDetails = Boolean(
		isConfigurePaymentAvailable && addPaymentDetailsExperienceUrl,
	);

	const hasPaymentDetailsEntered = Boolean(entitlement?.transactionAccount?.isBillToPresent);

	const trialTimeLeft = entitlement?.subscription?.trial?.timeLeft ?? undefined;
	const trialDaysLeft = getTrialDaysLeft(trialTimeLeft);

	const trialStartTimestamp = entitlement?.subscription?.trial?.startTimestamp;
	const trialStartDate = useMemo(
		() => (trialStartTimestamp ? new Date(trialStartTimestamp) : undefined),
		[trialStartTimestamp],
	);

	const trialEndTimestamp = entitlement?.subscription?.trial?.endTimestamp;
	const trialEndDate = useMemo(
		() => (trialEndTimestamp ? new Date(trialEndTimestamp) : undefined),
		[trialEndTimestamp],
	);

	const trialLengthDays = entitlement?.offering?.trial?.lengthDays ?? undefined;

	const isInTrial = Boolean(trialDaysLeft && trialDaysLeft > 0);

	const isInPredunning =
		entitlement?.preDunning?.status === 'IN_PRE_DUNNING' && invoiceableStatus === 'NOT_INVOICEABLE';

	const isInReverseTrial =
		(isInTrial || isInPredunning) &&
		(isInReverseTrialFromProduct !== undefined ? isInReverseTrialFromProduct : false);

	const isChangeOfferingAvailable =
		entitlement?.experienceCapabilities?.changeOfferingV2?.isAvailableToUser;
	const changeOfferingToStandardExperienceUrl =
		entitlement?.experienceCapabilities?.changeOfferingV2?.experienceUrl;
	const isDirectBuyChangeOfferingAvailable =
		entitlement?.experienceCapabilities?.changeOfferingV2WithSkipTrial?.isAvailableToUser;
	const directBuyChangeOfferingToStandardExperienceUrl =
		entitlement?.experienceCapabilities?.changeOfferingV2WithSkipTrial?.experienceUrl;

	const canUpgrade = Boolean(isChangeOfferingAvailable && changeOfferingToStandardExperienceUrl);
	const canDirectBuyUpgrade = Boolean(
		isDirectBuyChangeOfferingAvailable && directBuyChangeOfferingToStandardExperienceUrl,
	);

	const billingSourceSystem = getBillingSourceSystem(entitlement?.__typename);

	const transactionAccountId = entitlement?.transactionAccount?.key;

	const { isInCollection, collectionEntitlementId } = useMemo(() => {
		const collectionEntitlement = entitlement?.relatesFromEntitlements?.find(
			(item) => item?.relationshipType === 'COLLECTION',
		);

		return {
			isInCollection: !!collectionEntitlement?.entitlementId,
			collectionEntitlementId: collectionEntitlement?.entitlementId,
		};
	}, [entitlement?.relatesFromEntitlements]);

	const isEnterpriseCustomer = useMemo(() => {
		const enterpriseEntitlement = entitlement?.relatesFromEntitlements?.find(
			(item) => item?.relationshipType === 'ENTERPRISE',
		);

		return !!enterpriseEntitlement?.entitlementId;
	}, [entitlement?.relatesFromEntitlements]);

	const productCollection = getProductCollection(product);
	const collectionType =
		(isInCollection && productCollection?.isEnabled && productCollection?.collectionType) || null;

	const userRoles: UserRole[] = useMemo(() => {
		const roles: UserRole[] = [...(userRolesFromProduct || [])];
		if (
			data?.tenantContexts?.[0]?.entitlementInfo?.entitlement?.transactionAccount
				?.isCurrentUserBillingAdmin
		) {
			roles.push('BILLING_ADMIN');
		}
		return roles;
	}, [data?.tenantContexts, userRolesFromProduct]);

	const userEmail = data?.user?.email;

	// We need to use the `latestUsageForChargeElement` to get the licensed user count for both free and trialing customers
	const licensedUsersRaw = entitlement?.latestUsageForChargeElement;
	// Assumes that there are always at least one assigned user to cover scenarios
	const licensedUsers = Math.max(licensedUsersRaw || 0, 1);

	const storageUsageInGB = useMemo(() => {
		if (
			data.jira?.attachmentStorageUsed &&
			isProductOneOf(product, ['jira', 'jira-service-management', 'jira-product-discovery'])
		) {
			return bytesToGigabytes(data.jira.attachmentStorageUsed);
		}

		if (data.confluence_storage?.bytesUsed && product === 'confluence') {
			return bytesToGigabytes(data.confluence_storage.bytesUsed);
		}

		return undefined;
	}, [data.jira?.attachmentStorageUsed, data.confluence_storage?.bytesUsed, product]);
	const storageLimitInGB = useMemo(() => {
		if (data.jira?.attachmentStorageAllowed) {
			return bytesToGigabytes(data.jira.attachmentStorageAllowed);
		}
		return undefined;
	}, [data.jira?.attachmentStorageAllowed]);
	const storageIsUnlimited = useMemo(() => {
		if (data.jira?.attachmentStorageIsUnlimited) {
			return data.jira.attachmentStorageIsUnlimited;
		}
		return undefined;
	}, [data.jira?.attachmentStorageIsUnlimited]);

	const siteTraitsForContext: TraitsForContext = useMemo(
		() => getSiteTraitValues(siteTraits, product),
		[siteTraits, product],
	);

	const offeringKey = entitlement?.offering?.key;

	const automationFreeLimitStatus = useMemo(
		() =>
			fg('growth_automation_limits_kill_switch')
				? getAutomationFreeLimitStatus({
						product,
						edition,
						isInTrial,
						...siteTraitsForContext,
					})
				: null,
		[product, edition, isInTrial, siteTraitsForContext],
	);

	const lastKnownTrial: LastKnownTrial | undefined = useMemo(() => {
		// We purposely don't discard the results of the trial if the customer is not on a trial, the reason for this
		// is to enable experiences where the customer has just started a trial, but that change is not yet reflected
		// on the page that has loaded for them.
		const trialEndTimeStamp = entitlement?.entitlementProfile?.lastKnownTrial?.trialEndTimeStamp;

		if (!trialEndTimeStamp || trialEndTimeStamp <= Date.now()) {
			// Discard the result as it's in the past and we should assume outdated
			return undefined;
		}

		return {
			trialEndTimeStamp: entitlement?.entitlementProfile?.lastKnownTrial?.trialEndTimeStamp,
			trialType: entitlement?.entitlementProfile?.lastKnownTrial?.trialType,
			trialTrigger: entitlement?.entitlementProfile?.lastKnownTrial?.trialTrigger,
			createdAt: entitlement?.entitlementProfile?.lastKnownTrial?.createdAt,
			offeringId: entitlement?.entitlementProfile?.lastKnownTrial?.offeringId,
		};
	}, [entitlement?.entitlementProfile?.lastKnownTrial]);

	const isEnrolledInUpgradeFreePoolTargetExperiment: boolean = useMemo(() => {
		if (
			product !== 'confluence' ||
			edition !== 'standard' || // technically a tenant is enrolled if they are on 'free', but once they upgrade to standard we want to continue showing them the experience as there is a 'post-upgrade' UX
			isInTrial === false ||
			invoiceableStatus !== 'INVOICEABLE' ||
			!userRoles?.includes('SITE_ADMIN') ||
			lastKnownTrial?.trialType !== 'MANUAL_TRIAL'
		) {
			return false;
		}
		return isEnrolledInConfluenceUpgradeFreePoolTargetExperiment();
	}, [product, edition, isInTrial, invoiceableStatus, lastKnownTrial, userRoles]);

	const { startCheckingForAttemptToUpgrade } = useOnAttemptToUpgrade({
		product,
		edition,
		showFlag,
		// Fallback should never happen as we shouldn't call startCheckingForAttemptToUpgrade
		// when there is no entitlement
		entitlementId: entitlement?.id ?? 'entitlement-id',
		lastKnownTrial,
		isInCollection,
	});

	const hasDialogAutoOpened = useCallback(
		(dialogId: DialogId) => {
			if (dialogId === 'feature-onboarding-modal') {
				const state = data?.userPreferences?.onboardingState?.find(
					(s) => s.key === dialogIdToKey(dialogId, product, edition),
				);

				if (state?.value === 'true') {
					return true;
				}

				return localStorage.getItem(dialogIdToKey(dialogId, product, edition)) === 'true';
			}

			return true;
		},
		[data, product, edition],
	);

	const endUserRequestUpgradeLatestDate = useMemo(() => {
		const userPreference = data?.userPreferences?.onboardingState?.find(
			(s) => s.key === 'end-user-request-to-upgrade-latest-timestamp',
		);

		return typeof Number(userPreference?.value) === 'number' ? new Date(Number(userPreference?.value)) : null;
	}, [data]);

	const context: EditionAwarenessContextType = useMemo(() => {
		return {
			cloudId,
			product,
			edition,
			entitlementId,
			invoiceableStatus,
			canAddPaymentDetails,
			hasPaymentDetailsEntered,
			addPaymentDetailsExperienceUrl,
			offeringKey,
			trialDaysLeft,
			trialLengthDays,
			trialStartDate,
			trialEndDate,
			isInTrial,
			isInPredunning,
			isInReverseTrial,
			canUpgrade,
			canDirectBuyUpgrade,
			changeOfferingToStandardExperienceUrl,
			directBuyChangeOfferingToStandardExperienceUrl,
			transactionAccountId,
			isInCollection,
			isEnterpriseCustomer,
			collectionEntitlementId,
			collectionType,
			userRoles,
			userEmail,
			billingSourceSystem,
			environment,
			licensedUsers,
			storageUsageInGB,
			storageLimitInGB,
			storageIsUnlimited,
			...siteTraitsForContext,
			automationFreeLimitStatus,
			onOpenOnboardingMenu,
			premiumFeatureHighlightToggle,
			shouldAutoOpen,
			onAutoOpen,
			PremiumWrapper,
			startCheckingForAttemptToUpgrade,
			lastKnownTrial,
			getAction,
			showFlag,
			hideFlag,
			hasDialogAutoOpened,
			endUserRequestUpgradeLatestDate,
			isEnrolledInUpgradeFreePoolTargetExperiment,
		};
	}, [
		cloudId,
		product,
		edition,
		entitlementId,
		invoiceableStatus,
		canAddPaymentDetails,
		hasPaymentDetailsEntered,
		addPaymentDetailsExperienceUrl,
		offeringKey,
		trialDaysLeft,
		trialLengthDays,
		trialStartDate,
		trialEndDate,
		isInTrial,
		isInPredunning,
		isInReverseTrial,
		canUpgrade,
		canDirectBuyUpgrade,
		changeOfferingToStandardExperienceUrl,
		directBuyChangeOfferingToStandardExperienceUrl,
		transactionAccountId,
		isInCollection,
		isEnterpriseCustomer,
		collectionEntitlementId,
		collectionType,
		userRoles,
		userEmail,
		billingSourceSystem,
		environment,
		licensedUsers,
		storageUsageInGB,
		storageLimitInGB,
		storageIsUnlimited,
		siteTraitsForContext,
		automationFreeLimitStatus,
		onOpenOnboardingMenu,
		premiumFeatureHighlightToggle,
		shouldAutoOpen,
		onAutoOpen,
		PremiumWrapper,
		startCheckingForAttemptToUpgrade,
		lastKnownTrial,
		getAction,
		showFlag,
		hideFlag,
		hasDialogAutoOpened,
		endUserRequestUpgradeLatestDate,
		isEnrolledInUpgradeFreePoolTargetExperiment,
	]);

	const experimentContextValue = useMemo(() => new ExperimentContextClass(), []);

	if (!canRender) {
		return <UFOLoadHold name="edition-awareness-waiting-on-product" />;
	}

	return (
		<ExperimentContext.Provider value={experimentContextValue}>
			<EditionAwarenessContext.Provider value={context}>
				<ErrorBoundary product={product} edition={edition} id="audience-and-timing">
					<Spotlights>
						<AudienceAndTiming />
					</Spotlights>
				</ErrorBoundary>
			</EditionAwarenessContext.Provider>
		</ExperimentContext.Provider>
	);
}

export function EditionAwareness(props: EditionAwarenessProps): React.JSX.Element {
	const { product, edition } = props;

	return (
		<UFOSegment name="edition-awareness-platform">
			{/* This needs to be something that is not null, if it is null then the error will
				bubble up to the parent as `ErrorBoundary` will throw an exception if it is passed `null` */}
			<ErrorBoundary product={product} edition={edition} id="edition-awareness">
				<Suspense fallback={null}>
					<EditionAwarenessImpl {...props} />
				</Suspense>
			</ErrorBoundary>
		</UFOSegment>
	);
}
